import * as React from "react";
import { format, type Locale } from "date-fns";
import { Namespace, useDateFnsLocale, useTypedT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { ConnectProto } from "@racoon/api";
import type { Agreement, FieldSource } from "../../hooks/ttn/ttnDraftUtils";
import type { TripTimes } from "../../hooks/ttn/useTtnDraft";
import { useSourceHint } from "./ttn-source";

const TIME_PLACEHOLDER = "--:--";
const DATE_PLACEHOLDER = "-- - ----";

type StampProps = {
  label: string;
  date: Date | null | undefined;
  locale: Locale;
  /** TTN page: the value is boxed, its border saying where it came from. */
  hint?: { border: string; title?: string };
};

const Stamp: React.FC<StampProps> = ({ label, date, locale, hint }) => (
  <div className="flex items-center whitespace-nowrap">
    <span className="px-2 py-2 text-xs font-bold uppercase leading-4 text-ds-text-body">
      {label}
    </span>
    <span
      title={hint?.title}
      className={
        hint
          ? `flex h-6 items-center gap-2 rounded-md border border-ds-border-primary px-2 text-xs text-ds-text-body ${hint.border}`
          : "flex gap-4 p-2 text-xs font-medium leading-4 text-ds-text-body"
      }
    >
      <span>{date ? format(date, "HH:mm", { locale }) : TIME_PLACEHOLDER}</span>
      <span>{date ? format(date, "dd MMM yyyy", { locale }) : DATE_PLACEHOLDER}</span>
    </span>
  </div>
);

type Props = {
  route: ConnectProto.Route;
  /** TTN page: the trip's start / unload as the TTN shows them, with their sources. */
  times?: TripTimes;
};

/** Actual start / end of a route, shown under its header in the route sheet cascade. */
export const RouteFactTimesRow: React.FC<Props> = ({ route, times }) => {
  const t = useTypedT(Namespace.Routes);
  const locale = useDateFnsLocale();
  const sourceHint = useSourceHint();
  // A planned trip time is shown but not saved on approval: the plan is no fact.
  const hintOf = (source: FieldSource | undefined, agreement: Agreement | undefined) => {
    if (!times) return undefined;
    const hint = sourceHint(source, agreement);
    return source === "plan" ? { ...hint, title: t("ttnSourcePlanNotSaved") } : hint;
  };

  return (
    <div className="flex items-center justify-center gap-4 border-b border-ds-border-primary px-4 py-2">
      <Stamp
        label={t("colStarted")}
        date={times ? times.start : fromTimestamp(route.factStartAt)}
        locale={locale}
        hint={hintOf(times?.startSource, times?.startAgrees)}
      />
      <Stamp
        label={t("colFinished")}
        date={times ? times.end : fromTimestamp(route.factEndAt)}
        locale={locale}
        hint={hintOf(times?.endSource, times?.endAgrees)}
      />
    </div>
  );
};
